import React, { useEffect, useState, useMemo } from "react";
import ModalUI from "../legacy/Modal";
import { getFinancialTransactions, assignTransactionToInvoice, markInvoiceAsPaidCash } from "../../services/invoices";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { cn } from "@/lib/utils";

interface FinancialTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: number;
  onSuccess: () => void;
}

interface FinancialTransaction {
  id: number;
  transactionId: string;
  transactionKey: string;
  isPaymentSuccess: boolean;
  account: string;
  method: string;
  amount: number;
  fees: number;
  performedAt: string;
  event: string;
  customerFirstname?: string;
  customerLastname?: string;
  customerEmail?: string;
}

const formatDateTime = (value?: string) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const FinancialTransactionModal: React.FC<FinancialTransactionModalProps> = ({
  isOpen,
  onClose,
  invoiceId,
  onSuccess,
}) => {
  const [transactions, setTransactions] = useState<FinancialTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<FinancialTransaction | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<"cash" | "mobile">("cash");

  useEffect(() => {
    if (isOpen) {
      if (paymentMethod === "mobile") {
        loadTransactions();
      }
    } else {
      setSelectedTransaction(null);
      setError(null);
      setPaymentMethod("cash");
    }
  }, [isOpen, paymentMethod]);

  useEffect(() => {
    if (isOpen && paymentMethod === "mobile" && transactions.length === 0) {
      loadTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod, isOpen]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const response = await getFinancialTransactions();
      if (response.ok && response.data) {
        setTransactions(response.data);
      }
    } catch (error) {
      console.error("Error loading financial transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTransactionLabel = (transaction: FinancialTransaction) => {
    const parts = [];
    if (transaction.transactionKey) parts.push(`Key: ${transaction.transactionKey}`);
    if (transaction.amount) parts.push(`Montant: ${transaction.amount.toLocaleString()} FCFA`);
    if (transaction.account) parts.push(`Compte: ${transaction.account}`);
    if (transaction.customerFirstname || transaction.customerLastname) {
      parts.push(`Client: ${transaction.customerFirstname || ""} ${transaction.customerLastname || ""}`.trim());
    }
    return parts.join(" | ") || `Transaction #${transaction.id}`;
  };

  // Mapping en options Combobox : le spread `...t` préserve tous les champs
  // de la transaction (transactionKey, account, customerFirstname...) pour
  // que `renderItem` et `getFilterValue` puissent les lire sans aller
  // chercher dans `transactions` séparément.
  const transactionOptions = useMemo<ComboboxOption[]>(
    () =>
      transactions.map((t) => ({
        value: t.id,
        label: getTransactionLabel(t),
        ...t,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [transactions],
  );

  const handleSubmit = async () => {
    if (paymentMethod === "mobile" && !selectedTransaction?.transactionKey) {
      setError("Veuillez sélectionner une transaction");
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      let response;
      if (paymentMethod === "cash") {
        response = await markInvoiceAsPaidCash(invoiceId);
      } else {
        response = await assignTransactionToInvoice(invoiceId, selectedTransaction!.transactionKey);
      }

      if (response.ok) {
        onSuccess();
        onClose();
        setSelectedTransaction(null);
        setError(null);
        setPaymentMethod("cash");
      } else {
        setError(response.message || "Échec de l'opération");
      }
    } catch (error: any) {
      console.error("Error processing payment:", error);
      setError(error?.message || "Erreur lors du traitement du paiement");
    } finally {
      setSubmitting(false);
    }
  };

  const methodCardClass = (active: boolean) =>
    cn(
      "group relative flex flex-1 items-start gap-3 rounded-2xl border bg-white p-4 cursor-pointer transition-all duration-200",
      active
        ? "border-emerald-500 bg-emerald-50/40 ring-2 ring-emerald-500/20 shadow-sm"
        : "border-slate-200 hover:border-emerald-200 hover:bg-emerald-50/20",
    );

  const isSubmitDisabled =
    (paymentMethod === "mobile" && !selectedTransaction) || submitting;

  return (
    <ModalUI
      isOpen={isOpen}
      onClose={onClose}
      styles={{
        width: "92%",
        maxWidth: "880px",
        maxHeight: "90vh",
        overflow: "hidden",
      }}
    >
      <div className="flex h-full max-h-[90vh] flex-col">
        {/* Header sticky */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-slate-100 bg-white/95 px-6 py-5 backdrop-blur">
          <div className="flex items-center gap-3 min-w-0">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-sm ring-2 ring-white">
              <i className="bx bx-receipt text-lg" />
            </span>
            <div className="min-w-0">
              <span className="block text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                Paiement
              </span>
              <h2 className="text-lg font-semibold text-slate-900 truncate">
                Enregistrer le paiement
              </h2>
            </div>
            <span className="hidden sm:inline-flex shrink-0 items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-mono uppercase text-slate-600">
              <i className="bx bx-hash text-sm text-slate-400" />
              Facture {invoiceId}
            </span>
          </div>
          <button
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            aria-label="Fermer"
          >
            <i className="bx bx-x text-2xl" />
          </button>
        </div>

        {/* Body scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-500">
              <i className="bx bx-loader-alt bx-spin text-3xl text-emerald-500" />
              <span className="text-sm">Chargement des transactions…</span>
            </div>
          ) : (
            <>
              {/* Section 1 — Méthode de paiement */}
              <section className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                    Méthode de paiement
                  </span>
                  <span className="flex-1 h-px bg-slate-100" />
                </div>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <label className={methodCardClass(paymentMethod === "cash")}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="cash"
                      checked={paymentMethod === "cash"}
                      onChange={(e) => {
                        setPaymentMethod(e.target.value as "cash" | "mobile");
                        setSelectedTransaction(null);
                        setError(null);
                      }}
                      className="sr-only"
                    />
                    <span
                      className={cn(
                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors",
                        paymentMethod === "cash"
                          ? "bg-emerald-500 text-white shadow-sm"
                          : "bg-slate-100 text-slate-500 group-hover:bg-emerald-100 group-hover:text-emerald-600",
                      )}
                    >
                      <i className="bx bx-money text-xl" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800">Espèces</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Encaissement direct, marqué payé immédiatement.
                      </p>
                    </div>
                    {paymentMethod === "cash" && (
                      <i className="bx bx-check-circle absolute right-3 top-3 text-emerald-500 text-lg" />
                    )}
                  </label>

                  <label className={methodCardClass(paymentMethod === "mobile")}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="mobile"
                      checked={paymentMethod === "mobile"}
                      onChange={(e) => {
                        setPaymentMethod(e.target.value as "cash" | "mobile");
                        setSelectedTransaction(null);
                        setError(null);
                      }}
                      className="sr-only"
                    />
                    <span
                      className={cn(
                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors",
                        paymentMethod === "mobile"
                          ? "bg-emerald-500 text-white shadow-sm"
                          : "bg-slate-100 text-slate-500 group-hover:bg-emerald-100 group-hover:text-emerald-600",
                      )}
                    >
                      <i className="bx bx-mobile-alt text-xl" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800">
                        Paiement mobile existant
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Rattacher une transaction FedaPay déjà reçue.
                      </p>
                    </div>
                    {paymentMethod === "mobile" && (
                      <i className="bx bx-check-circle absolute right-3 top-3 text-emerald-500 text-lg" />
                    )}
                  </label>
                </div>
              </section>

              {/* Section 2 — Combobox transaction */}
              {paymentMethod === "mobile" && (
                <section className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                      Transaction FedaPay
                    </span>
                    <span className="flex-1 h-px bg-slate-100" />
                  </div>
                  <Combobox
                    options={transactionOptions}
                    value={selectedTransaction?.id ?? null}
                    onChange={(opt) => {
                      const tx = transactions.find((t) => t.id === opt.value) ?? null;
                      setSelectedTransaction(tx);
                      setError(null);
                    }}
                    placeholder="Rechercher une transaction (clé, montant, compte, client…)"
                    searchPlaceholder="Tapez pour rechercher…"
                    emptyText="Aucune transaction trouvée"
                    className="w-full"
                    // z-[130] : la modale (ModalUI legacy) est à z-[120].
                    // Sans cet override, le PopoverContent shadcn (z-50 par
                    // défaut, rendu en portail body) passe DERRIÈRE la
                    // modale et les items ne sont plus cliquables.
                    popoverClassName="z-[130] max-h-[420px]"
                    getFilterValue={(opt) =>
                      [
                        opt.transactionKey,
                        opt.transactionId,
                        opt.account,
                        opt.customerFirstname,
                        opt.customerLastname,
                        opt.customerEmail,
                        opt.amount?.toString(),
                        opt.method,
                      ]
                        .filter(Boolean)
                        .join(" ")
                    }
                    renderItem={(option) => (
                      <div className="w-full py-1">
                        <div className="flex items-start justify-between gap-3 mb-1.5">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-slate-800 truncate">
                              {option.transactionKey || `Transaction #${option.id}`}
                            </div>
                            <div className="text-xs text-slate-500 truncate">
                              {option.customerFirstname || option.customerLastname
                                ? `${option.customerFirstname || ""} ${option.customerLastname || ""}`.trim()
                                : option.account || "—"}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <span className="text-sm font-bold text-emerald-700 tabular-nums">
                              {option.amount?.toLocaleString()} FCFA
                            </span>
                            <span
                              className={cn(
                                "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium",
                                option.isPaymentSuccess
                                  ? "border-emerald-100 bg-emerald-50 text-emerald-700"
                                  : "border-rose-100 bg-rose-50 text-rose-700",
                              )}
                            >
                              <i
                                className={
                                  option.isPaymentSuccess
                                    ? "bx bx-check"
                                    : "bx bx-x"
                                }
                              />
                              {option.isPaymentSuccess ? "Réussi" : "Échoué"}
                            </span>
                          </div>
                        </div>
                        <div className="text-[11px] text-slate-400 flex flex-wrap gap-x-3 gap-y-0.5">
                          {option.method && (
                            <span>
                              <i className="bx bx-credit-card text-xs mr-0.5 align-middle" />
                              {option.method}
                            </span>
                          )}
                          {option.fees ? (
                            <span>
                              <i className="bx bx-coin text-xs mr-0.5 align-middle" />
                              Frais {option.fees.toLocaleString()} FCFA
                            </span>
                          ) : null}
                          {option.performedAt && (
                            <span>
                              <i className="bx bx-time-five text-xs mr-0.5 align-middle" />
                              {formatDateTime(option.performedAt)}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  />
                </section>
              )}

              {/* Section 3 — Récap transaction sélectionnée */}
              {paymentMethod === "mobile" && selectedTransaction && (
                <section className="rounded-2xl border border-emerald-100 bg-emerald-50/30 p-5 space-y-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-emerald-600 shadow-sm ring-1 ring-emerald-100">
                        <i className="bx bx-check text-xl" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                          Transaction sélectionnée
                        </p>
                        <p className="text-sm font-semibold text-slate-800 truncate">
                          {selectedTransaction.transactionKey || `#${selectedTransaction.id}`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                        Montant
                      </p>
                      <p className="text-lg font-bold text-emerald-700 tabular-nums">
                        {selectedTransaction.amount?.toLocaleString()} FCFA
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <div className="flex justify-between sm:block">
                      <span className="text-slate-500 text-xs">Identifiant de transaction</span>
                      <span className="font-medium text-slate-800 sm:block">
                        {selectedTransaction.transactionId || "—"}
                      </span>
                    </div>
                    <div className="flex justify-between sm:block">
                      <span className="text-slate-500 text-xs">Méthode</span>
                      <span className="font-medium text-slate-800 sm:block">
                        {selectedTransaction.method || "—"}
                      </span>
                    </div>
                    <div className="flex justify-between sm:block">
                      <span className="text-slate-500 text-xs">Compte</span>
                      <span className="font-medium text-slate-800 sm:block">
                        {selectedTransaction.account || "—"}
                      </span>
                    </div>
                    <div className="flex justify-between sm:block">
                      <span className="text-slate-500 text-xs">Frais</span>
                      <span className="font-medium text-slate-800 sm:block tabular-nums">
                        {selectedTransaction.fees != null
                          ? `${selectedTransaction.fees.toLocaleString()} FCFA`
                          : "—"}
                      </span>
                    </div>
                    <div className="flex justify-between sm:block">
                      <span className="text-slate-500 text-xs">Date</span>
                      <span className="font-medium text-slate-800 sm:block">
                        {formatDateTime(selectedTransaction.performedAt)}
                      </span>
                    </div>
                    <div className="flex justify-between sm:block">
                      <span className="text-slate-500 text-xs">Statut</span>
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium w-fit",
                          selectedTransaction.isPaymentSuccess
                            ? "border-emerald-100 bg-emerald-50 text-emerald-700"
                            : "border-rose-100 bg-rose-50 text-rose-700",
                        )}
                      >
                        <i
                          className={
                            selectedTransaction.isPaymentSuccess
                              ? "bx bx-check-circle"
                              : "bx bx-x-circle"
                          }
                        />
                        {selectedTransaction.isPaymentSuccess ? "Réussi" : "Échoué"}
                      </span>
                    </div>
                    {(selectedTransaction.customerFirstname ||
                      selectedTransaction.customerLastname ||
                      selectedTransaction.customerEmail) && (
                      <div className="sm:col-span-2 flex justify-between sm:block">
                        <span className="text-slate-500 text-xs">Client</span>
                        <span className="font-medium text-slate-800 sm:block">
                          {`${selectedTransaction.customerFirstname || ""} ${selectedTransaction.customerLastname || ""}`.trim() ||
                            "—"}
                          {selectedTransaction.customerEmail && (
                            <span className="text-slate-500 font-normal">
                              {" "}
                              ({selectedTransaction.customerEmail})
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* Error */}
              {error && (
                <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4">
                  <i className="bx bx-error-circle text-rose-500 text-xl mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-rose-800">Erreur</p>
                    <p className="text-sm text-rose-700 mt-0.5">{error}</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer sticky */}
        <div className="sticky bottom-0 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t border-slate-100 bg-white px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-5 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className={cn(
              "inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/30 shadow-lg shadow-green-600/25",
              isSubmitDisabled
                ? "bg-slate-300 shadow-none cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700 active:bg-green-800",
            )}
          >
            {submitting ? (
              <>
                <i className="bx bx-loader-alt bx-spin text-base" />
                En cours…
              </>
            ) : paymentMethod === "cash" ? (
              <>
                <i className="bx bx-check-circle text-base" />
                Marquer comme payé
              </>
            ) : (
              <>
                <i className="bx bx-link text-base" />
                Assigner la transaction
              </>
            )}
          </button>
        </div>
      </div>
    </ModalUI>
  );
};

export default FinancialTransactionModal;
